import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalFilter, usePagination, useTable } from "react-table";
import { IoEyeOutline, IoAddOutline } from "react-icons/io5";
import { FiEdit, FiRefreshCcw, FiSearch, FiX, FiUpload } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import $ from "jquery";
import ApiURl from "../../controllers/Api";

/* ----------------------------- Small UI Helpers ---------------------------- */

const cn = (...classes) => classes.filter(Boolean).join(" ");

const IconButton = ({ className, children, ...props }) => (
  <button
    type="button"
    className={cn(
      "inline-flex items-center justify-center rounded-xl border border-[#d8c6b7] bg-white p-2",
      "text-[#5d4b44] hover:bg-[#fff7ed] active:bg-[#f3e7dc] transition",
      "focus:outline-none focus:ring-4 focus:ring-[#d4af37]/10 focus:border-[#c59a4a]",
      className
    )}
    {...props}
  >
    {children}
  </button>
);

const Badge = ({ tone = "gray", children }) => {
  const tones = {
    green: "bg-[#edf7ee] text-[#47704e] border-[#cfe2d1]",
    red: "bg-[#fff0ee] text-[#9d3d36] border-[#edcbc7]",
    gray: "bg-[#f3ebe4] text-[#6d5a53] border-[#dfd1c6]",
    blue: "bg-[#eef2f7] text-[#4d6683] border-[#d6dde7]",
    yellow: "bg-[#fff7df] text-[#8a651b] border-[#ead9a7]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        tones[tone] || tones.gray
      )}
    >
      {children}
    </span>
  );
};

/* ----------------------------- Tab Components ---------------------------- */

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      "rounded-t-xl px-4 py-2.5 text-sm font-medium transition",
      active 
        ? "bg-[#fffaf5] text-[#2B0000] border-b-2 border-[#c59a4a]" 
        : "text-white/70 hover:text-white hover:bg-white/10"
    )}
  >
    {children}
  </button>
);

/* ---------------------------------- Toast --------------------------------- */

const Toast = ({ toast, onClose }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!toast?.open) return;
    timerRef.current = setTimeout(() => onClose?.(), toast.duration ?? 2500);
    return () => clearTimeout(timerRef.current);
  }, [toast?.open, toast?.duration, onClose]);

  if (!toast?.open) return null;

  const tone =
    toast.type === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : toast.type === "error"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : toast.type === "warning"
      ? "border-yellow-200 bg-yellow-50 text-yellow-800"
      : "border-gray-200 bg-white text-gray-900";

  return (
    <div className="fixed bottom-5 right-5 z-[60]">
      <div
        className={cn(
          "min-w-[260px] max-w-[340px] rounded-2xl border p-4 shadow-[0_18px_45px_rgba(43,0,0,0.16)]",
          "backdrop-blur supports-[backdrop-filter]:bg-white/90",
          tone
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="text-sm font-semibold">{toast.title}</div>
            {toast.message ? (
              <div className="mt-1 text-xs opacity-90">{toast.message}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-lg p-1 hover:bg-black/5"
            onClick={onClose}
            aria-label="Close toast"
          >
            <FiX />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------- Modal --------------------------------- */

const Modal = ({ open, title, children, onClose, footer, size = "max-w-lg" }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#2B0000]/35 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className={cn(
            "w-full overflow-hidden rounded-[26px] border border-[#d4af37]/25 bg-[#fffaf5] shadow-[0_30px_90px_rgba(43,0,0,0.22)]",
            size
          )}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="flex items-center justify-between gap-3 border-b border-[#d4af37]/20 bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-5 py-4">
            <div className="font-serif text-xl font-medium text-[#2B0000]">{title}</div>
            <IconButton onClick={onClose} aria-label="Close modal">
              <FiX />
            </IconButton>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-5 py-5">{children}</div>

          {footer ? (
            <div className="border-t border-[#d8c6b7] bg-[#fbf3eb] px-5 py-4">{footer}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmTone = "danger",
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const confirmBtn =
    confirmTone === "danger"
      ? "bg-[#a2463d] hover:bg-[#8f3932]"
      : confirmTone === "success"
      ? "bg-[#6f7f5e] hover:bg-[#5f704f]"
      : "bg-[#2B0000] hover:bg-[#4A090B]";

  return (
    <Modal
      open={open}
      title={title}
      onClose={loading ? undefined : onCancel}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className={cn(
              "rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000]",
              "hover:bg-[#fff7ed] transition",
              loading ? "opacity-60 cursor-not-allowed" : ""
            )}
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            className={cn(
              "rounded-xl px-4 py-2 text-sm font-semibold text-white transition inline-flex items-center gap-2",
              confirmBtn,
              loading ? "opacity-70 cursor-not-allowed" : ""
            )}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <AiOutlineLoading3Quarters className="animate-spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      }
    >
      <div className="text-sm leading-relaxed text-[#5d4b44]">{description}</div>
    </Modal>
  );
};

/* ------------------------------- Global Filter ------------------------------ */

const GlobalFilter = ({ globalFilter, setGlobalFilter, placeholder = "Search..." }) => (
  <div className="w-full sm:w-[320px]">
    <div className="relative">
      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9b877e]" />
      <input
        value={globalFilter || ""}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-2xl border border-[#d6c4b5] bg-white text-[#2B0000] placeholder:text-[#9b877e]",
          "pl-9 pr-3 py-2.5 text-sm",
          "focus:outline-none focus:ring-4 focus:ring-[#d4af37]/10 focus:border-[#c59a4a]"
        )}
      />
    </div>
  </div>
);

/* ------------------------------- Pagination UI ------------------------------ */

const PaginationBar = ({
  pageIndex,
  pageCount,
  pageSize,
  setPageSize,
  canPreviousPage,
  canNextPage,
  previousPage,
  nextPage,
  gotoPage,
  totalRows,
  pageRowsCount,
}) => {
  const start = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const end = totalRows === 0 ? 0 : pageIndex * pageSize + pageRowsCount;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-[#5d4b44]">
        Showing <span className="font-semibold">{start}-{end}</span> of{" "}
        <span className="font-semibold">{totalRows}</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between sm:justify-end">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#5d4b44]">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className={cn(
              "rounded-xl border border-[#d6c4b5] bg-white px-2 py-1.5 text-sm text-[#2B0000]",
              "focus:outline-none focus:ring-4 focus:ring-[#d4af37]/10 focus:border-[#c59a4a]"
            )}
          >
            {[5, 10, 20, 30, 50].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <IconButton
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            className={!canPreviousPage ? "opacity-50 cursor-not-allowed" : ""}
            aria-label="First page"
          >
            {"<<"}
          </IconButton>
          <IconButton
            onClick={previousPage}
            disabled={!canPreviousPage}
            className={!canPreviousPage ? "opacity-50 cursor-not-allowed" : ""}
            aria-label="Previous page"
          >
            {"<"}
          </IconButton>

          <div className="text-sm text-[#4a352f]">
            Page <span className="font-semibold">{pageIndex + 1}</span> /{" "}
            <span className="font-semibold">{pageCount}</span>
          </div>

          <IconButton
            onClick={nextPage}
            disabled={!canNextPage}
            className={!canNextPage ? "opacity-50 cursor-not-allowed" : ""}
            aria-label="Next page"
          >
            {">"}
          </IconButton>
          <IconButton
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            className={!canNextPage ? "opacity-50 cursor-not-allowed" : ""}
            aria-label="Last page"
          >
            {">>"}
          </IconButton>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------- Image Upload Component ---------------------------- */

const ImageUpload = ({ currentImage, onImageChange, onImageRemove, label = "Upload Image", type = "main" }) => {
  const [preview, setPreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setImageError(false);
      };
      reader.readAsDataURL(file);
      onImageChange(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setImageError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageRemove();
  };

  // Construct image URL based on type
  const getImageUrl = () => {
    if (preview) return preview;
    if (currentImage && !imageError) {
      return type === 'sub' 
        ? `${ApiURl}/images/subcategories/${currentImage}`
        : `${ApiURl}/images/${currentImage}`;
    }
    return null;
  };

  const imageUrl = getImageUrl();

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[#4a352f]">{label}</label>
      <div className="flex items-start gap-4">
        {imageUrl ? (
          <div className="relative">
            <img 
              src={imageUrl} 
              alt="Preview" 
              className="h-24 w-24 rounded-xl border border-[#d8c6b7] bg-[#fffaf5] object-contain shadow-sm"
              onError={handleImageError}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -right-2 -top-2 rounded-full bg-[#a2463d] p-1 text-white shadow-md transition hover:bg-[#8f3932]"
            >
              <FiX size={14} />
            </button>
          </div>
        ) : (
          <div className="flex h-24 w-24 items-center justify-center rounded-xl border-2 border-dashed border-[#d4af37]/35 bg-[#fbf3eb]">
            <FiUpload className="text-[#a28f86]" size={24} />
          </div>
        )}
        
        <div className="flex-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-medium text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
          >
            <FiUpload size={16} />
            Choose File
          </label>
          <p className="mt-1 text-xs text-[#7d6c64]">PNG, JPG, GIF up to 2MB</p>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------- Main Category Form --------------------------- */

const MainCategoryForm = ({ initialData = null, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    category_name: initialData?.category_name || '',
    is_active: initialData?.is_active?.toString() ?? '1',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, imageFile, imageRemoved);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#4a352f]">
          Category Name <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          value={formData.category_name}
          onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
          className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
          placeholder="Enter category name"
          required
          minLength={3}
          maxLength={50}
        />
      </div>

      <ImageUpload
        currentImage={!imageRemoved ? initialData?.sample_image : null}
        onImageChange={setImageFile}
        onImageRemove={() => {
          setImageFile(null);
          setImageRemoved(true);
        }}
        label="Category Image"
        type="main"
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#4a352f]">Status</label>
        <select
          value={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
          className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
        >
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-medium text-[#2B0000] transition hover:bg-[#fff7ed]"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-medium text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
          disabled={loading}
        >
          {loading && <AiOutlineLoading3Quarters className="animate-spin" />}
          {initialData ? 'Update Category' : 'Add Category'}
        </button>
      </div>
    </form>
  );
};

/* ---------------------------- SubCategory Form --------------------------- */

const SubCategoryForm = ({ initialData = null, mainCategories, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    category_name: initialData?.category_name || '',
    parent_id: initialData?.parent_id || '',
    is_active: initialData?.is_active?.toString() ?? '1',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, imageFile, imageRemoved);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#4a352f]">
          Subcategory Name <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          value={formData.category_name}
          onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
          className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
          placeholder="Enter subcategory name"
          required
          minLength={3}
          maxLength={50}
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#4a352f]">
          Main Category <span className="text-rose-500">*</span>
        </label>
        <select
          value={formData.parent_id}
          onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
          className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
          required
        >
          <option value="">Select Main Category</option>
          {mainCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.category_name}
            </option>
          ))}
        </select>
      </div>

      <ImageUpload
        currentImage={!imageRemoved ? initialData?.sample_image : null}
        onImageChange={setImageFile}
        onImageRemove={() => {
          setImageFile(null);
          setImageRemoved(true);
        }}
        label="Subcategory Image"
        type="sub"
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-[#4a352f]">Status</label>
        <select
          value={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
          className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
        >
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-medium text-[#2B0000] transition hover:bg-[#fff7ed]"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-medium text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
          disabled={loading}
        >
          {loading && <AiOutlineLoading3Quarters className="animate-spin" />}
          {initialData ? 'Update Subcategory' : 'Add Subcategory'}
        </button>
      </div>
    </form>
  );
};

/* ---------------------------- Category Details View --------------------------- */

const CategoryDetails = ({ item, type, mainCategories = [] }) => {
  const [imageError, setImageError] = useState(false);

  const imageUrl = item?.sample_image && !imageError
    ? type === 'sub' 
      ? `${ApiURl}/images/subcategories/${item.sample_image}`
      : `${ApiURl}/images/${item.sample_image}`
    : null;

  const handleImageError = () => {
    setImageError(true);
  };

  // Get the correct ID based on type
  const itemId = type === 'sub' ? item?.category_id : item?.id;

  return (
    <div className="space-y-4">
      {imageUrl && (
        <div className="flex justify-center">
          <img 
            src={imageUrl} 
            alt={item?.category_name}
            className="h-32 w-32 rounded-2xl border border-[#d8c6b7] bg-[#fffaf5] object-contain shadow-md"
            onError={handleImageError}
          />
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-medium text-[#7d6c64]">ID</div>
          <div className="text-sm font-semibold text-[#2B0000]">#{itemId}</div>
        </div>
        
        <div>
          <div className="text-xs font-medium text-[#7d6c64]">Status</div>
          <div className="mt-1">
            {item?.is_active === "1" || item?.is_active === 1 ? (
              <Badge tone="green">Active</Badge>
            ) : (
              <Badge tone="red">Inactive</Badge>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs font-medium text-[#7d6c64]">Name</div>
        <div className="text-sm font-semibold text-[#2B0000]">{item?.category_name}</div>
      </div>

    </div>
  );
};

/* ---------------------------- Main Component --------------------------- */

const CategoryList = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('main');

  // State for data
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    duration: 2500,
  });

  // Modal states
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    mode: "view",
    type: "main",
    item: null,
  });

  const [formModal, setFormModal] = useState({
    open: false,
    mode: "add",
    type: "main",
    item: null,
  });

  // Confirm dialog state
  const [confirmState, setConfirmState] = useState({
    open: false,
    type: "main",
    itemId: null,
    itemName: "",
    currentStatus: "0",
    newStatus: "0",
  });

  const showToast = useCallback((payload) => {
    setToast({
      open: true,
      type: payload.type ?? "info",
      title: payload.title ?? "",
      message: payload.message ?? "",
      duration: payload.duration ?? 2500,
    });
  }, []);

  const closeToast = useCallback(() => {
    setToast((t) => ({ ...t, open: false }));
  }, []);

  // Fetch main categories
  const fetchMainCategories = useCallback(async () => {
    try {
      const response = await $.getJSON(`${ApiURl}/getCatData.php`);
      console.log("Main categories response:", response);
      
      // Handle different response structures
      let rows = [];
      if (response?.data && Array.isArray(response.data)) {
        rows = response.data;
      } else if (Array.isArray(response)) {
        rows = response;
      } else if (response?.data && typeof response.data === 'object') {
        rows = [response.data];
      }
      
      setMainCategories(rows);
    } catch (err) {
      console.error("Error fetching main categories:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to load main categories.",
      });
    }
  }, [showToast]);

  // Fetch subcategories
  const fetchSubCategories = useCallback(async () => {
    try {
      const response = await $.getJSON(`${ApiURl}/getSubCatData.php`);
      console.log("Subcategories response:", response);
      
      // Handle different response structures
      let rows = [];
      if (response?.data && Array.isArray(response.data)) {
        rows = response.data;
      } else if (Array.isArray(response)) {
        rows = response;
      } else if (response?.data && typeof response.data === 'object') {
        rows = [response.data];
      }
      
      setSubCategories(rows);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to load subcategories.",
      });
    }
  }, [showToast]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      await Promise.all([fetchMainCategories(), fetchSubCategories()]);
    } catch (err) {
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fetchMainCategories, fetchSubCategories]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Add/Update main category
  const handleMainCategorySubmit = useCallback(async (formData, imageFile, imageRemoved) => {
    const isEditing = formModal.mode === 'edit';
    const categoryId = formModal.item?.id || formModal.item?.category_id || "";

    try {
      setActionLoadingId(categoryId || 'new');
      
      const submitData = new FormData();
      
      if (isEditing) {
        submitData.append('category_id', categoryId);
      }
      
      submitData.append('category_name', formData.category_name);
      submitData.append('is_active', formData.is_active);
      
      if (imageFile) {
        submitData.append('image', imageFile);
      } else if (imageRemoved && isEditing) {
        submitData.append('remove_image', '1');
      }

      const url = isEditing 
        ? `${ApiURl}/updateCategory.php`
        : `${ApiURl}/addCategory.php`;

      const response = await $.ajax({
        url,
        type: 'POST',
        data: submitData,
        processData: false,
        contentType: false,
        dataType: 'json',
      });

      if (response.status === 'success' || response.success) {
        showToast({
          type: 'success',
          title: 'Success',
          message: response.message || 'Category saved successfully',
        });
        
        setFormModal({ open: false, mode: 'add', type: 'main', item: null });
        await fetchAllData();
      } else {
        throw new Error(response.message || 'Failed to save category');
      }
    } catch (err) {
      console.error('Error saving main category:', err);
      showToast({
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to save category.',
      });
    } finally {
      setActionLoadingId(null);
    }
  }, [formModal.mode, formModal.item, fetchAllData, showToast]);

 // Add/Update subcategory
const handleSubCategorySubmit = useCallback(async (formData, imageFile, imageRemoved) => {
  const isEditing = formModal.mode === 'edit';
  const categoryId = formModal.item?.category_id || formModal.item?.id;

  console.log("Submitting subcategory:", {
    isEditing,
    categoryId,
    formData,
    imageFile,
    imageRemoved,
    originalItem: formModal.item
  });

  try {
    setActionLoadingId(categoryId || 'new');
    
    const submitData = new FormData();
    
    if (isEditing) {
      submitData.append('category_id', categoryId);
    }
    
    submitData.append('category_name', formData.category_name);
    submitData.append('parent_id', formData.parent_id);
    submitData.append('is_active', formData.is_active);
    
    if (imageFile) {
      submitData.append('image', imageFile);
    } else if (imageRemoved && isEditing) {
      submitData.append('remove_image', '1');
    }

    // Log FormData contents
    for (let pair of submitData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const url = isEditing 
      ? `${ApiURl}/updateSubCategory.php`
      : `${ApiURl}/addSubCategory.php`;

    const response = await $.ajax({
      url,
      type: 'POST',
      data: submitData,
      processData: false,
      contentType: false,
      dataType: 'json',
    });

    console.log("Server response:", response);

    if (response.status === 'success' || response.success) {
      showToast({
        type: 'success',
        title: 'Success',
        message: response.message || 'Subcategory saved successfully',
      });
      
      setFormModal({ open: false, mode: 'add', type: 'sub', item: null });
      
      // If we have updated data in response, update the local state
      if (response.data) {
        console.log("Updating local state with:", response.data);
        setSubCategories(prev => {
          if (isEditing) {
            // Update existing item
            return prev.map(item => 
              (item.category_id === response.data.category_id || item.id === response.data.category_id) 
                ? response.data 
                : item
            );
          } else {
            // Add new item
            return [response.data, ...prev];
          }
        });
      }
      
      // Still fetch all data to ensure consistency
      await fetchAllData();
    } else {
      throw new Error(response.message || 'Failed to save subcategory');
    }
  } catch (err) {
    console.error('Error saving subcategory:', err);
    showToast({
      type: 'error',
      title: 'Error',
      message: err.message || 'Failed to save subcategory.',
    });
  } finally {
    setActionLoadingId(null);
  }
}, [formModal.mode, formModal.item, fetchAllData, showToast]);

  // Toggle status
  const requestToggle = useCallback((type, item) => {
    const newStatus = item.is_active === "1" || item.is_active === 1 ? "0" : "1";
    const itemId = type === 'sub' ? item.category_id : item.id;
    
    setConfirmState({
      open: true,
      type,
      itemId: itemId,
      itemName: item.category_name,
      currentStatus: item.is_active,
      newStatus,
    });
  }, []);

  const performToggle = useCallback(async () => {
    const { type, itemId, newStatus, itemName } = confirmState;
    if (!itemId) return;

    try {
      setActionLoadingId(itemId);
      setConfirmState((s) => ({ ...s, open: false }));

      // Optimistic update
      if (type === 'main') {
        setMainCategories(prev =>
          prev.map((c) => (c.id === itemId ? { ...c, is_active: newStatus } : c))
        );
      } else {
        setSubCategories(prev =>
          prev.map((c) => (c.category_id === itemId ? { ...c, is_active: newStatus } : c))
        );
      }

      const formData = new FormData();
      formData.append('category_id', itemId);
      formData.append('is_active', newStatus);

      const url = type === 'main' 
        ? `${ApiURl}/updateCategory.php`
        : `${ApiURl}/updateSubCategory.php`;

      const response = await $.ajax({
        url,
        type: 'POST',
        data: formData,
        processData: false,
        contentType: false,
        dataType: 'json',
      });

      if (response.success || response.status === "success") {
        showToast({
          type: "success",
          title: "Status Updated",
          message: `${itemName} is now ${newStatus === "1" ? "Active" : "Inactive"}.`,
        });
      } else {
        throw new Error(response.message || "Update failed");
      }

      await fetchAllData();
    } catch (err) {
      console.error("Error updating status:", err);
      await fetchAllData();
      showToast({
        type: "error",
        title: "Update failed",
        message: err?.message || "Something went wrong.",
      });
    } finally {
      setActionLoadingId(null);
    }
  }, [confirmState, fetchAllData, showToast]);

  // Table columns for main categories
  const mainColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ value }) => <span className="font-semibold text-[#2B0000]">#{value}</span>,
      },
      {
        Header: "CATEGORY NAME",
        accessor: "category_name",
        Cell: ({ value }) => <span className="font-semibold text-[#2B0000]">{value}</span>,
      },
      {
        Header: "IMAGE",
        accessor: "sample_image",
        Cell: ({ value, row }) => {
          const [imageError, setImageError] = useState(false);
          
          if (!value || imageError) {
            return <span className="text-gray-400 text-xs">No image</span>;
          }
          
          return (
            <img 
              src={`${ApiURl}/images/${value}`} 
              alt={row.original.category_name}
              className="h-11 w-11 rounded-xl border border-[#d8c6b7] bg-[#fffaf5] object-contain shadow-sm"
              onError={() => setImageError(true)}
            />
          );
        },
      },
      {
        Header: "STATUS",
        accessor: "is_active",
        Cell: ({ value }) => (
          value === "1" || value === 1 ? 
            <Badge tone="green">Active</Badge> : 
            <Badge tone="red">Inactive</Badge>
        ),
      },
      {
        Header: "ACTIONS",
        id: "actions",
        Cell: ({ row }) => {
          const item = row.original;
          const isBusy = actionLoadingId === item.id;

          return (
            <div className="flex items-center justify-end gap-2">
              <IconButton 
                aria-label="View" 
                onClick={() => setDetailsModal({ open: true, mode: "view", type: "main", item })}
              >
                <IoEyeOutline />
              </IconButton>

              <IconButton 
                aria-label="Edit" 
                onClick={() => setFormModal({ open: true, mode: "edit", type: "main", item })}
              >
                <FiEdit />
              </IconButton>

            </div>
          );
        },
      },
    ],
    [actionLoadingId, requestToggle]
  );

  // Table columns for subcategories
  const subColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "category_id",
        Cell: ({ value }) => <span className="font-semibold text-[#2B0000]">#{value}</span>,
      },
      {
        Header: "SUBCATEGORY NAME",
        accessor: "category_name",
        Cell: ({ value }) => <span className="font-semibold text-[#2B0000]">{value}</span>,
      },
      {
        Header: "IMAGE",
        accessor: "sample_image",
        Cell: ({ value, row }) => {
          const [imageError, setImageError] = useState(false);
          
          if (!value || imageError) {
            return <span className="text-gray-400 text-xs">No image</span>;
          }
          
          return (
            <img 
              src={`${ApiURl}/images/subcategories/${value}`} 
              alt={row.original.category_name}
              className="h-11 w-11 rounded-xl border border-[#d8c6b7] bg-[#fffaf5] object-contain shadow-sm"
              onError={() => setImageError(true)}
            />
          );
        },
      },
      {
        Header: "STATUS",
        accessor: "is_active",
        Cell: ({ value }) => (
          value === "1" || value === 1 ? 
            <Badge tone="green">Active</Badge> : 
            <Badge tone="red">Inactive</Badge>
        ),
      },
      {
        Header: "ACTIONS",
        id: "actions",
        Cell: ({ row }) => {
          const item = row.original;
          const isBusy = actionLoadingId === (item.category_id || item.id);

          return (
            <div className="flex items-center justify-end gap-2">
              <IconButton 
                aria-label="View" 
                onClick={() => setDetailsModal({ open: true, mode: "view", type: "sub", item })}
              >
                <IoEyeOutline />
              </IconButton>

              <IconButton 
                aria-label="Edit" 
                onClick={() => setFormModal({ open: true, mode: "edit", type: "sub", item })}
              >
                <FiEdit />
              </IconButton>

            </div>
          );
        },
      },
    ],
    [actionLoadingId, requestToggle]
  );

  // Table instances
  const mainTableInstance = useTable(
    {
      columns: mainColumns,
      data: mainCategories,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    usePagination
  );

  const subTableInstance = useTable(
    {
      columns: subColumns,
      data: subCategories,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    usePagination
  );

  const activeTable = activeTab === 'main' ? mainTableInstance : subTableInstance;
  const activeData = activeTab === 'main' ? mainCategories : subCategories;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
    gotoPage,
    setPageSize,
  } = activeTable;

  const { pageIndex, pageSize, globalFilter } = state;

  useEffect(() => {
    gotoPage(0);
  }, [globalFilter, activeTab, gotoPage]);

  return (
    <div className="category-list-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] font-[Kanit] text-[#2B0000]">
      <div className="mx-auto max-w-[1500px] px-3 py-4 sm:px-5 sm:py-6 lg:px-7">
        <div className="overflow-hidden rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
          {/* Header */}
          <div className="bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.18),transparent_34%),linear-gradient(135deg,#5a2020,#2B0000)] px-5 py-5 text-white sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl font-medium text-[#f4dfae] sm:text-3xl">Category Management</h2>
                <p className="mt-1 text-sm text-white/65">Manage product categories</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <GlobalFilter 
                  globalFilter={globalFilter} 
                  setGlobalFilter={setGlobalFilter}
                  placeholder="Search categories..."
                />

                <button
                  type="button"
                  onClick={() => setFormModal({ 
                    open: true, 
                    mode: "add", 
                    type: "main", 
                    item: null 
                  })}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#6f7f5e] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#5f704f]"
                >
                  <IoAddOutline />
                  Add Category
                </button>

                <button
                  type="button"
                  onClick={fetchAllData}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:bg-[#fff7ed]"
                >
                  <FiRefreshCcw />
                  Refresh
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-5 flex gap-1 border-b border-white/10">
              <TabButton 
                active={activeTab === 'main'} 
                onClick={() => setActiveTab('main')}
              >
                Main Categories ({mainCategories.length})
              </TabButton>
            </div>
          </div>

          {/* Body */}
          <div className="p-0">
            {error ? (
              <div className="px-5 sm:px-6 py-4">
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  {error}
                </div>
              </div>
            ) : null}

            {loading ? (
              <div className="px-5 sm:px-6 py-5">
                <div className="animate-pulse rounded-2xl border border-[#d8c6b7] bg-white p-6 shadow-sm">
                  <div className="mb-4 h-5 w-48 rounded bg-[#eadfd5]" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-10 rounded-2xl bg-[#f0e6dd]" />
                    ))}
                  </div>
                </div>
              </div>
            ) : activeData.length === 0 ? (
              <div className="px-5 sm:px-6 py-5">
                <div className="rounded-2xl border border-[#d8c6b7] bg-white p-6 text-center shadow-sm">
                  <div className="text-sm text-[#5d4b44]">
                    No categories found.
                  </div>
                  <button
                    onClick={() => setFormModal({ 
                      open: true, 
                      mode: "add", 
                      type: "main", 
                      item: null 
                    })}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-[#6f7f5e] transition hover:text-[#5f704f]"
                  >
                    <IoAddOutline />
                    Add your first category
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Pagination */}
                <div className="border-b border-[#d8c6b7] bg-[#fbf3eb] px-5 py-4 sm:px-6">
                  <PaginationBar
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    gotoPage={gotoPage}
                    totalRows={activeData.length}
                    pageRowsCount={page.length}
                  />
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table {...getTableProps()} className="w-full min-w-[1100px] text-left text-sm text-[#5d4b44]">
                    <thead className="bg-[#eee2d7] text-xs uppercase tracking-[0.1em] text-[#4b352f]">
                      {headerGroups.map((headerGroup) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                        return (
                          <tr key={key} {...headerGroupProps}>
                            {headerGroup.headers.map((column) => {
                              const { key: columnKey, ...columnProps } = column.getHeaderProps();
                              return (
                                <th
                                  key={columnKey}
                                  {...columnProps}
                                  className={cn(
                                    "whitespace-nowrap border-b border-[#d8c6b7] px-5 py-4 font-semibold sm:px-6",
                                    column.id === "actions" ? "text-right" : ""
                                  )}
                                >
                                  {column.render("Header")}
                                </th>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </thead>

                    <tbody {...getTableBodyProps()} className="divide-y divide-[#eadfd5]">
                      {page.map((row) => {
                        prepareRow(row);
                        const { key: rowKey, ...rowProps } = row.getRowProps();
                        return (
                          <tr key={rowKey} {...rowProps} className="transition hover:bg-[#fff5ea]">
                            {row.cells.map((cell) => {
                              const { key: cellKey, ...cellProps } = cell.getCellProps();
                              return (
                                <td
                                  key={cellKey}
                                  {...cellProps}
                                  className={cn(
                                    "px-5 sm:px-6 py-4 align-middle",
                                    cell.column.id === "actions" ? "text-right" : ""
                                  )}
                                >
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer tip */}
                <div className="flex flex-col gap-2 border-t border-[#d8c6b7] bg-[#fbf3eb] px-5 py-3 text-xs text-[#6f5e57] sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <span>
                    Tip: Use search to filter quickly. (Filtered total:{" "}
                    <span className="font-semibold text-[#2B0000]">{activeData.length}</span>)
                  </span>
                  <span className="text-[#7d6c64]">On mobile, swipe left/right to see all columns.</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal - Add/Edit */}
      <Modal
        open={formModal.open}
        title={formModal.mode === "add" ? "Add Category" : "Edit Category"}
        onClose={() => setFormModal({ open: false, mode: "add", type: "main", item: null })}
        size="max-w-2xl"
      >
        <MainCategoryForm
          initialData={formModal.item}
          onSubmit={handleMainCategorySubmit}
          onCancel={() => setFormModal({ open: false, mode: "add", type: "main", item: null })}
          loading={actionLoadingId === (formModal.item?.id || 'new')}
        />
      </Modal>

      {/* Details Modal */}
      <Modal
        open={detailsModal.open}
        title="Category Details"
        onClose={() => setDetailsModal({ open: false, mode: "view", type: "main", item: null })}
        size="max-w-md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
              onClick={() => setDetailsModal({ open: false, mode: "view", type: "main", item: null })}
            >
              Close
            </button>

            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] transition hover:bg-[#4A090B]"
              onClick={() => {
                setDetailsModal({ open: false, mode: "view", type: "main", item: null });
                setFormModal({ 
                  open: true, 
                  mode: "edit", 
                  type: detailsModal.type, 
                  item: detailsModal.item 
                });
              }}
            >
              <FiEdit />
              Edit
            </button>
          </div>
        }
      >
        <CategoryDetails 
          item={detailsModal.item} 
          type={detailsModal.type}
          mainCategories={mainCategories}
        />
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.newStatus === "1" ? "Activate item?" : "Deactivate item?"}
        description={
          <>
            You're about to{" "}
            <span className="font-semibold">{confirmState.newStatus === "1" ? "activate" : "deactivate"}</span>{" "}
            <span className="font-semibold text-[#2B0000]">{confirmState.itemName || "this item"}</span>. This will
            change its status immediately.
          </>
        }
        confirmLabel={confirmState.newStatus === "1" ? "Activate" : "Deactivate"}
        confirmTone={confirmState.newStatus === "1" ? "success" : "danger"}
        loading={actionLoadingId === confirmState.itemId}
        onCancel={() => setConfirmState((s) => ({ ...s, open: false }))}
        onConfirm={performToggle}
      />

      <Toast toast={toast} onClose={closeToast} />

      <style>{`
        .category-list-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .category-list-luxury input,
        .category-list-luxury select,
        .category-list-luxury button {
          font: inherit;
        }

        .category-list-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .category-list-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .category-list-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.52) rgba(212,175,55,.07);
        }

        .category-list-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .category-list-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .category-list-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.52);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .category-list-luxury {
            padding-bottom: 18px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .category-list-luxury *,
          .category-list-luxury *::before,
          .category-list-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CategoryList;